import type { FC } from 'react'
import { memo, useEffect, useState } from 'react'

import { CommentsAPI } from '@webapp/api/CommentsAPI'
import { Chat } from '../Chat'
import { type LegacyComment } from '../Chat/Chat'
import { CommentStyled } from './styles/CommentStyled'

export interface ICommentProps {
  contentID: number
  objectID: number
  userID: number
  onCommentUpdate?: (comments: LegacyComment[]) => void
  initialComments?: LegacyComment[]
  readOnly?: boolean
  pageSize?: number
}

export const Comment: FC<ICommentProps> = memo(
  ({
    contentID,
    objectID,
    userID,
    onCommentUpdate,
    initialComments = [],
    readOnly,
    pageSize,
  }) => {
    const [isSubmitting, setSubmitting] = useState(false)
    const [isLoading, setLoading] = useState(false)
    const [comments, setComments] = useState<LegacyComment[]>(initialComments)

    useEffect(() => {
      setLoading(true)

      CommentsAPI.getComments(contentID, objectID, {
        order_by: 'id',
        page_size: pageSize,
      })
        .then((res) => res.data)
        .then((data) => {
          setLoading(false)
          setComments(data)

          onCommentUpdate(data)
        })
        .catch(() => {
          setLoading(false)
        })
    }, [])

    const onSubmit = (comment, { clear }) => {
      setSubmitting(true)

      CommentsAPI.createComment({
        comment,
        content_type: contentID,
        object_pk: objectID,
        user_url: window.location.href,
      })
        .then((res) => res.data)
        .then((newComment) => {
          clear()

          const newComments = [...comments, newComment]
          setComments(newComments)

          onCommentUpdate(newComments)

          setSubmitting(false)
        })
        .catch(() => {
          setSubmitting(false)
        })
    }

    return (
      <CommentStyled>
        <Chat
          comments={comments}
          receiverID={userID}
          onChatSubmit={onSubmit}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          readOnly={readOnly}
        />
      </CommentStyled>
    )
  }
)

Comment.defaultProps = {
  onCommentUpdate: () => undefined,
}
Comment.displayName = 'Comment'
